// THE SIGN-IN SEAM FOR ONE PROPPEAL ACCOUNT (ACC-2). Every decision about who is signed in is made HERE, once.
//
// Tyler, 2026-09-30 (`docs/legal/WEB_PURCHASE_POLICIES.md`, `e6ae17df`): one account everywhere — the website and the
// app share one Firebase Auth pool and one credit ledger. The website offers Continue with Apple first, then email and
// password. The design is ACCOUNTS_PLAN.md §2.1–2.5; this file is its web half, with no screen behind it yet (ACC-3a).
//
// ── THE SHAPE ─────────────────────────────────────────────────────────────────────────────────────────────────────────
//
//   `AuthBackend`       THE PORT. One Firebase SDK call per method and no decisions. Its one real implementation is
//                       `firebaseAuthBackend` in `services/firebaseSdk.ts` (the only file that imports `firebase`); its
//                       fake is `createFakeAuthBackend` in `services/fakeAuth.ts`, which models the SDK and the pool.
//   `AccountEmails`     THE SERVER'S EMAILS (E2–E6). Firebase's own senders are never called: the approved emails are
//                       ours. ACC-1 implements these as server ops; until then the real one answers `emailsUnavailable`.
//   `createAuthSession` THE DECISIONS: when to sign in anonymously, link or sign in, which refusal a Firebase code is,
//                       when a re-entry is required, and the order an account ends in. The real session and the fake
//                       are both this function, over different backends, so the fake cannot decide differently.
//
// ── WHAT THE SDK DOES THAT THE ORDER BELOW DEPENDS ON (firebase 12.19.0, `@firebase/auth` 1.13.6, measured) ────────────
//
//   • `signInAnonymously` returns the current user ONLY IF THAT USER IS ANONYMOUS; otherwise it creates a new anonymous
//     user and signs in as it (`dist/esm/index-4NFEPWkC.js:5740-5760`). So start-up waits for the restored state
//     (`authStateReady`, :3003) and signs in anonymously only when nobody is signed in (F-AC-1).
//   • `revokeAccessToken` DOES NOTHING, SILENTLY, WHEN NO USER IS SIGNED IN: it needs the current user's ID token
//     (:3019-3034). So Apple's token is revoked BEFORE the Firebase user is deleted, never after.
//   • `user.delete()` ALWAYS signs the auth out, whether or not that user is the current one (:1843-1853). So an
//     anonymous user left behind by signing in to an existing account is NOT deleted from this browser: that would sign
//     the account out. It is reported (`LookAround.leftBehind`) for the server to deal with.
//   • Linking refreshes the user's tokens and reloads it (`_forOperation`, :5689-5690), so no forced refresh follows.
//   • A credential that is already another account's comes back as `auth/credential-already-in-use` carrying the
//     server's response (`_makeTaggedError`, :1043-1057), which `OAuthProvider.credentialFromError` turns back into a
//     credential (:4967-4993). Whether Apple's response carries one is for staging (ACC-0/ACC-1); a missing one is
//     `appleCredentialUnavailable`, never a silent failure.
//
// NO SENTENCE LIVES HERE. Every outcome is a typed code; ACC-3a maps them to the approved copy (C1–C12, D5).

/** Where a refusal came from, for ACC-3a's copy. Firebase's `code` for an error, when it has one. */
export function authErrorCode(error: unknown): string | null {
  if (typeof error === "object" && error !== null && "code" in error) {
    const code = (error as { code: unknown }).code;
    if (typeof code === "string") return code;
  }
  return null;
}

// ── THE PORT ────────────────────────────────────────────────────────────────────────────────────────────────────────

export const APPLE_PROVIDER_ID = "apple.com";
export const PASSWORD_PROVIDER_ID = "password";

/** The code the real backend throws when `OAuthProvider.credentialFromError` finds no credential to sign in with. */
export const NO_PENDING_CREDENTIAL = "proppeal/no-pending-credential";

/** The SDK's current user, as this seam reads it. */
export interface AuthUser {
  readonly uid: string;
  readonly isAnonymous: boolean;
  readonly email: string | null;
  /** The user object's flag. Stale after a verification until `reload` — the SDK's behaviour, modelled by the fake. */
  readonly emailVerified: boolean;
  /** `providerData[].providerId`: `password`, `apple.com`. Empty for an anonymous user. */
  readonly providerIds: readonly string[];
}

/** What an Apple popup handed back that this seam keeps: Apple's OAuth access token, for revocation on delete. */
export interface AppleGrant {
  readonly accessToken: string | null;
}

/** `checkActionCode`'s answer. For VERIFY_AND_CHANGE_EMAIL, `email` is the NEW address (the SDK's own mapping). */
export interface ActionCodeInfo {
  readonly operation: string;
  readonly email: string | null;
  readonly previousEmail: string | null;
}

/**
 * THE PORT: one Firebase call per method. Anything that decides — which call, in what order, what a failure means —
 * belongs in `createAuthSession`, not here. Methods THROW Firebase's errors (`code: "auth/…"`) unchanged.
 */
export interface AuthBackend {
  /** `auth.authStateReady()`: resolves once the persisted sign-in has been restored (or found absent). */
  authStateReady(): Promise<void>;
  /** `auth.currentUser`. */
  currentUser(): AuthUser | null;
  /** `signInAnonymously(auth)` — which REPLACES a non-anonymous user (see the header). */
  signInAnonymously(): Promise<void>;
  /** `currentUser.getIdToken(forceRefresh)`. */
  getIdToken(forceRefresh?: boolean): Promise<string>;
  /** `reload(currentUser)`. */
  reload(): Promise<void>;
  /** `linkWithCredential(currentUser, EmailAuthProvider.credential(email, password))`. Keeps the uid. */
  linkWithPassword(email: string, password: string): Promise<void>;
  /** `signInWithEmailAndPassword(auth, email, password)`. */
  signInWithPassword(email: string, password: string): Promise<void>;
  /** `linkWithPopup(currentUser, new OAuthProvider("apple.com"))`. Keeps the uid. */
  linkWithApple(): Promise<AppleGrant>;
  /** `signInWithCredential(auth, OAuthProvider.credentialFromError(error))`; throws `NO_PENDING_CREDENTIAL` without one. */
  signInWithCredentialFromError(error: unknown): Promise<AppleGrant>;
  /** `reauthenticateWithCredential(currentUser, EmailAuthProvider.credential(currentUser.email, password))`. */
  reauthenticateWithPassword(password: string): Promise<void>;
  /** `reauthenticateWithPopup(currentUser, new OAuthProvider("apple.com"))`. */
  reauthenticateWithApple(): Promise<AppleGrant>;
  /** `updatePassword(currentUser, newPassword)`. */
  updatePassword(newPassword: string): Promise<void>;
  /** `applyActionCode(auth, oobCode)`. */
  applyActionCode(oobCode: string): Promise<void>;
  /** `checkActionCode(auth, oobCode)`. */
  checkActionCode(oobCode: string): Promise<ActionCodeInfo>;
  /** `verifyPasswordResetCode(auth, oobCode)`: the account's email. */
  verifyPasswordResetCode(oobCode: string): Promise<string>;
  /** `confirmPasswordReset(auth, oobCode, newPassword)`. */
  confirmPasswordReset(oobCode: string, newPassword: string): Promise<void>;
  /** `revokeAccessToken(auth, token)` — a no-op when nobody is signed in (see the header). */
  revokeAppleAccessToken(accessToken: string): Promise<void>;
  /** `deleteUser(currentUser)`. THROWS when Firebase refuses or nobody is signed in. Signs the auth out. */
  deleteUser(): Promise<void>;
  /** `signOut(auth)`. */
  signOut(): Promise<void>;
}

// ── THE SERVER'S EMAILS (E2–E6) ─────────────────────────────────────────────────────────────────────────────────────

/**
 * THE ACCOUNT EMAILS, SENT BY OUR SERVER (ACCOUNTS_PLAN §2.7; approved E2–E6). The server generates each link with the
 * Admin SDK (`generateEmailVerificationLink`, `generatePasswordResetLink`, `generateVerifyAndChangeEmailLink`) and sends
 * our words. The caller is the server's to read from the ID token; nothing here names a uid.
 */
export interface AccountEmails {
  /** E2, to the signed-in account's email. */
  sendVerification(): Promise<OpResult>;
  /** E3. The same answer whether or not an account uses the address (D2). */
  sendPasswordReset(email: string): Promise<OpResult>;
  /** E4, to the new address; the change happens when its link is opened. */
  sendEmailChange(newEmail: string): Promise<OpResult>;
  /** E6, after the password changed. Firebase has no sender for this at all. */
  sendPasswordChanged(): Promise<OpResult>;
}

/** Until ACC-1 builds the server ops, the real session sends nothing — and SAYS so, by code. It never falls back to
 *  Firebase's own senders, whose words are not the approved ones. */
export const UNBUILT_ACCOUNT_EMAILS: AccountEmails = {
  sendVerification: async () => ({ ok: false, refusal: { kind: "emailsUnavailable" } }),
  sendPasswordReset: async () => ({ ok: false, refusal: { kind: "emailsUnavailable" } }),
  sendEmailChange: async () => ({ ok: false, refusal: { kind: "emailsUnavailable" } }),
  sendPasswordChanged: async () => ({ ok: false, refusal: { kind: "emailsUnavailable" } }),
};

// ── THE TYPED RESULTS (codes for ACC-3a, never sentences) ───────────────────────────────────────────────────────────

export type SignInProvider = "anonymous" | "password" | "apple";

/** Why an operation did not happen. The approved sentence each maps to is in the comment; ACC-3a does the mapping. */
export type AuthRefusal =
  /** C2 — creating a password account with an email that already has an account. */
  | { readonly kind: "emailHasAccount" }
  /** C10 — Apple, but a password account already has Apple's email. */
  | { readonly kind: "emailHasPasswordAccount" }
  /** C11 — adding Apple to this account, and that Apple ID is a different account. Nothing was switched. */
  | { readonly kind: "appleIdIsAnotherAccount" }
  /** C1 — wrong password or no such account (one answer for both). */
  | { readonly kind: "wrongPasswordOrNoAccount" }
  /** C3 */
  | { readonly kind: "tooManyTries" }
  /** C4 — under `MIN_PASSWORD_LENGTH`, or refused by the project's password policy. */
  | { readonly kind: "passwordTooShort" }
  /** C5 */
  | { readonly kind: "notAnEmail" }
  /** C6 — this needs a fresh re-entry first (`reauthenticate`). */
  | { readonly kind: "reauthRequired" }
  /** C9 */
  | { readonly kind: "network" }
  /** D5 — an action link that has expired or was already used. */
  | { readonly kind: "linkExpired" }
  /** The owner closed Apple's window. Not an error to show. */
  | { readonly kind: "popupClosed" }
  /** The browser blocked Apple's window. */
  | { readonly kind: "popupBlocked" }
  /** Apple answered for a different Apple ID than this account's (a re-entry with the wrong one). */
  | { readonly kind: "wrongAccount" }
  /** Creating or signing in while an account is already signed in: sign out first. */
  | { readonly kind: "alreadySignedIn" }
  /** This account already has that way to sign in. */
  | { readonly kind: "alreadyLinked" }
  /** Needs an account, and this browser has only a look-around (or nobody). */
  | { readonly kind: "notSignedIn" }
  /** The account has no password (or no Apple) to re-enter with. */
  | { readonly kind: "wrongProvider" }
  /** The email is already confirmed; nothing was sent. */
  | { readonly kind: "alreadyVerified" }
  /** The account is disabled. */
  | { readonly kind: "accountDisabled" }
  /** Apple's ID is another account's, and Firebase handed back no credential to sign in to it with. */
  | { readonly kind: "appleCredentialUnavailable" }
  /** The server's account emails are not available: none wired into this session, switched off on the server
   *  (WEB_ACCOUNTS_ENABLED), or unable to send right now (ACC-1, `services/accountEmails.ts`). */
  | { readonly kind: "emailsUnavailable" }
  /** A Firebase code with no mapping. Its appearance is an alarm: a code was added without copy (R112). */
  | { readonly kind: "unknown"; readonly code: string | null };

export type OpResult = { readonly ok: true } | { readonly ok: false; readonly refusal: AuthRefusal };

/**
 * WHAT HAPPENED TO THE LOOK-AROUND (ACCOUNTS_PLAN §2.3). Everything this client and the server keep is keyed by uid.
 *   carriedOver — the anonymous user was LINKED: same uid, so this tab's appeal and anything under it came along.
 *   leftBehind  — the owner signed in to an EXISTING account: the look-around stays on `anonymousUid`. ACC-3a re-runs
 *                 the address step under the account and forgets this tab's appeal id (B7). The anonymous user is NOT
 *                 deleted here (see the header), and its data is not purged here either: an anonymous website visitor
 *                 can still pay today, until ACC-1's checkout refuses anonymous callers.
 *   none        — there was no anonymous session to carry.
 */
export type LookAround =
  | { readonly kind: "carriedOver" }
  | { readonly kind: "leftBehind"; readonly anonymousUid: string }
  | { readonly kind: "none" };

export type AccountResult =
  | { readonly ok: true; readonly uid: string; readonly provider: SignInProvider; readonly lookAround: LookAround }
  | { readonly ok: false; readonly refusal: AuthRefusal };

export type SignOutResult =
  /** Signed out, and a fresh anonymous session started under a NEW uid. ACC-3a clears the tab's appeal id. */
  | { readonly ok: true; readonly anonymousUid: string }
  /** `signedOut` says whether the account's session ended before the failure. */
  | { readonly ok: false; readonly signedOut: boolean; readonly refusal: AuthRefusal };

export type ChangePasswordResult =
  /** Changed. `notice` is E6's send, which never undoes the change (a failed notice is degraded, not blocking). */
  | { readonly ok: true; readonly notice: OpResult }
  | { readonly ok: false; readonly refusal: AuthRefusal };

/** How the sign-in ended after the account was deleted: the Firebase user DELETED (what iOS does), or — when Firebase
 *  refused that — SIGNED OUT, so this browser keeps no session for an account whose data is gone; `failed` only if both
 *  were refused. */
export type AuthEnding = "deleted" | "signedOut" | "failed";

/** Apple's token on delete (F-AC-7, web half): revoked; the revoke failed; or no token was held to revoke (the screen
 *  skipped the Apple re-entry). The last two DEGRADE — the deletion goes on — and alarm. */
export type AppleRevocation = "revoked" | "failed" | "noToken";

// ── ACTION LINKS (`/account/action`, ACC-3a) ────────────────────────────────────────────────────────────────────────

/** The modes our emails' links carry. Firebase's own names, as its action handler receives them. */
export type ActionMode = "verifyEmail" | "resetPassword" | "recoverEmail" | "verifyAndChangeEmail";

export interface ActionLink {
  readonly mode: ActionMode;
  readonly oobCode: string;
}

const ACTION_MODES: readonly ActionMode[] = ["verifyEmail", "resetPassword", "recoverEmail", "verifyAndChangeEmail"];

/** Reads an action link's query. The route hands the parameters in; this package never reads the page's location. */
export function parseActionLink(params: URLSearchParams): ActionLink | null {
  const mode = params.get("mode");
  const oobCode = (params.get("oobCode") ?? "").trim();
  if (mode === null || oobCode === "" || !ACTION_MODES.includes(mode as ActionMode)) return null;
  return { mode: mode as ActionMode, oobCode };
}

export type ActionLinkResult =
  /** E2's link: the email is confirmed. */
  | { readonly ok: true; readonly kind: "emailVerified" }
  /** E4's link: the sign-in email is now `email`. */
  | { readonly ok: true; readonly kind: "emailChanged"; readonly email: string | null }
  /** The undo link after a change: `email` is the address restored. D-flow: reset the password next. */
  | { readonly ok: true; readonly kind: "emailRestored"; readonly email: string | null }
  /** E3's link is good: ask for a new password for `email`, then call `resetPassword`. */
  | { readonly ok: true; readonly kind: "choosePassword"; readonly email: string }
  | { readonly ok: false; readonly refusal: AuthRefusal };

// ── THE SESSION ─────────────────────────────────────────────────────────────────────────────────────────────────────

/** The website's password rule (ACCOUNTS_PLAN §2.5, NIST SP 800-63B): at least 8 characters, nothing else. Firebase's
 *  default minimum is 6, so this check is what holds the rule until the project's password policy (ACC-0) says 8 too. */
export const MIN_PASSWORD_LENGTH = 8;

/** How long a re-entry counts for the operations that need one (ACCOUNTS_PLAN §2.5: changing email or password, and
 *  deleting). Ours, not Firebase's: Firebase separately refuses with `auth/requires-recent-login`, mapped the same. */
export const REAUTH_WINDOW_MS = 5 * 60 * 1000;

export interface AuthSession {
  /** The current user's id, or null before sign-in completes (and after the account is deleted). */
  readonly uid: string | null;
  /** How this browser is signed in. `password` when the account has a password (with or without Apple). */
  readonly provider: SignInProvider | null;
  readonly email: string | null;
  /** Read-only. False for a password account until its email is confirmed; Apple accounts arrive confirmed. */
  readonly emailVerified: boolean;
  /** How this account re-enters (C6): Apple whenever Apple is linked — only an Apple re-entry yields the token deletion
   *  must revoke — otherwise its password. Null for a look-around. */
  readonly reauthenticateWith: "password" | "apple" | null;
  /** What happened to Apple's token on the last deletion, or null if none was needed. */
  readonly appleRevocation: AppleRevocation | null;
  /** ACC-3b (G10, "Ways to sign in"): the ways this account can sign in, in the website's names. Empty for a
   *  look-around and for nobody. */
  readonly linkedProviders: readonly ("password" | "apple")[];

  /** Firebase Auth ID token for the `Authorization: Bearer` header. */
  getIdToken(): Promise<string>;
  /** START-UP (F-AC-1): wait for the restored sign-in, and sign in anonymously ONLY when nobody is. Idempotent. */
  signIn(): Promise<void>;
  /** Re-read the user and fetch a fresh token — after a link opened elsewhere, so the server sees `email_verified`. */
  refresh(): Promise<void>;

  /** Turns the look-around into a password account by LINKING it: the uid, and everything under it, carries over. */
  createAccountWithPassword(email: string, password: string): Promise<AccountResult>;
  signInWithPassword(email: string, password: string): Promise<AccountResult>;
  /** Apple's popup. A look-around LINKS to Apple; if that Apple ID is already an account, it SIGNS IN to that account.
   *  On a password account it adds Apple, and an Apple ID that is another account is refused, never switched to. */
  continueWithApple(): Promise<AccountResult>;
  /** Signs out, then starts a fresh anonymous session under a new uid. */
  signOut(): Promise<SignOutResult>;
  /** The re-entry C6 asks for: the account's password, or Apple's popup. */
  reauthenticate(proof: { readonly password: string } | { readonly apple: true }): Promise<OpResult>;
  changePassword(newPassword: string): Promise<ChangePasswordResult>;
  /** ACC-3b (G10, "Add a password"): links an email-and-password sign-in, under the account's own email, to an account
   *  that has none (an Apple account). Needs a fresh re-entry first (C6), as a password change does. */
  addPassword(password: string): Promise<OpResult>;
  /** Verify-before-update, through our server (E4): nothing changes until the new address's link is opened. */
  changeEmail(newEmail: string): Promise<OpResult>;
  sendEmailVerification(): Promise<OpResult>;
  sendPasswordReset(email: string): Promise<OpResult>;
  /** Opens an action link from one of our emails. */
  openActionLink(link: ActionLink): Promise<ActionLinkResult>;
  /** Finishes a reset opened with `openActionLink` (`choosePassword`). */
  resetPassword(oobCode: string, newPassword: string): Promise<OpResult>;
  /**
   * ACC-1 (F-ACC2-1): REVOKE APPLE'S TOKEN BEFORE ASKING THE SERVER TO DELETE THE ACCOUNT. With WEB_ACCOUNTS_ENABLED on,
   * `v2Credits` `deleteAccount` deletes the Firebase Auth user itself, after the purge — and `revokeAccessToken` needs a
   * user that still exists. So the delete screen calls this FIRST (after the Apple re-entry), then `deleteAccount`, then
   * `endAfterDeletion`. Answers what happened to Apple's token, or null for an account without Apple. A revoke that
   * fails never stops the deletion (degrade, alarm), exactly as before.
   */
  revokeAppleBeforeDeletion(): Promise<AppleRevocation | null>;
  /**
   * ENDS THE SIGN-IN AFTER THE SERVER HAS DELETED THE ACCOUNT (WEB-PAY-2, F-WP-2) — iOS's order exactly
   * (`AuthStore.deleteAccount`: `postDeleteAccount()`, and only on its success `user.delete()`). The caller calls this
   * ONLY after `v2Credits` `deleteAccount` answered 200; a refused purge ends nothing. When Apple is linked and was not
   * revoked before the call, Apple's token is revoked FIRST, while the user still exists (F-AC-7).
   *
   * ACC-1: pass the answer's `signInDeleted`. True means the server has ALREADY deleted the sign-in, so this only signs
   * this browser out (a `user.delete()` now would fail, and a revoke now could not reach a deleted user) and answers
   * "deleted"; false (the server's flag off) is the order above, unchanged.
   */
  endAfterDeletion(options?: { readonly signInDeleted?: boolean }): Promise<AuthEnding>;
}

/** The two things `endAccountSession` does to a user, whichever SDK stands behind them. */
export interface EndableAccount {
  deleteUser(): Promise<void>;
  signOut(): Promise<void>;
}

/** THE ONE PLACE THE ORDER IS WRITTEN: delete the user; if Firebase refuses, sign out. Used by the real session and by
 *  the fake alike, so the fake cannot end a session in a way the real one does not. */
export async function endAccountSession(account: EndableAccount): Promise<AuthEnding> {
  try {
    await account.deleteUser();
    return "deleted";
  } catch {
    // Firebase can refuse a delete (auth/requires-recent-login). iOS shows that refusal; here the purge has already
    // succeeded, so the least this browser can do is stop holding a session for it.
  }
  try {
    await account.signOut();
    return "signedOut";
  } catch {
    return "failed";
  }
}

/** THE START-UP ORDER (F-AC-1), shared by `firebaseSdk.ts`'s `start` and every session. Firebase restores a signed-in
 *  user asynchronously; signing in anonymously before that, or regardless of it, replaces a real account. */
export async function ensureSignedIn(backend: Pick<AuthBackend, "authStateReady" | "currentUser" | "signInAnonymously">): Promise<void> {
  await backend.authStateReady();
  if (backend.currentUser() === null) await backend.signInAnonymously();
}

/** The one table from a Firebase code to a refusal. Codes whose meaning depends on the operation (already-in-use) are
 *  decided where they occur, before this is consulted. */
const REFUSAL_BY_CODE: Readonly<Record<string, AuthRefusal>> = {
  "auth/email-already-in-use": { kind: "emailHasAccount" },
  "auth/account-exists-with-different-credential": { kind: "emailHasPasswordAccount" },
  "auth/invalid-credential": { kind: "wrongPasswordOrNoAccount" },
  "auth/invalid-login-credentials": { kind: "wrongPasswordOrNoAccount" },
  "auth/wrong-password": { kind: "wrongPasswordOrNoAccount" },
  "auth/user-not-found": { kind: "wrongPasswordOrNoAccount" },
  "auth/too-many-requests": { kind: "tooManyTries" },
  "auth/weak-password": { kind: "passwordTooShort" },
  "auth/password-does-not-meet-requirements": { kind: "passwordTooShort" },
  "auth/invalid-email": { kind: "notAnEmail" },
  "auth/missing-email": { kind: "notAnEmail" },
  "auth/requires-recent-login": { kind: "reauthRequired" },
  "auth/network-request-failed": { kind: "network" },
  "auth/expired-action-code": { kind: "linkExpired" },
  "auth/invalid-action-code": { kind: "linkExpired" },
  "auth/popup-closed-by-user": { kind: "popupClosed" },
  "auth/cancelled-popup-request": { kind: "popupClosed" },
  "auth/user-cancelled": { kind: "popupClosed" },
  "auth/popup-blocked": { kind: "popupBlocked" },
  "auth/user-mismatch": { kind: "wrongAccount" },
  "auth/provider-already-linked": { kind: "alreadyLinked" },
  "auth/user-disabled": { kind: "accountDisabled" },
  [NO_PENDING_CREDENTIAL]: { kind: "appleCredentialUnavailable" },
};

export function refusalFor(error: unknown): AuthRefusal {
  const code = authErrorCode(error);
  return (code !== null ? REFUSAL_BY_CODE[code] : undefined) ?? { kind: "unknown", code };
}

function refused(refusal: AuthRefusal): { ok: false; refusal: AuthRefusal } {
  return { ok: false, refusal };
}

/** The provider a user is signed in with, as this website names it. A password wins when both are linked: it is the
 *  one the owner can type. Null for a provider this website does not offer. */
export function providerOf(user: AuthUser | null): SignInProvider | null {
  if (user === null) return null;
  if (user.isAnonymous) return "anonymous";
  if (user.providerIds.includes(PASSWORD_PROVIDER_ID)) return "password";
  if (user.providerIds.includes(APPLE_PROVIDER_ID)) return "apple";
  return null;
}

export interface AuthSessionOptions {
  readonly backend: AuthBackend;
  readonly emails: AccountEmails;
  /** The token reader for the Bearer header. Default: the backend's. The real session passes the seam's guarded one. */
  readonly getIdToken?: () => Promise<string>;
  /** The clock the re-entry window reads. */
  readonly now?: () => number;
}

/** THE SESSION: the real one over `firebaseAuthBackend`, the fake over `createFakeAuthBackend`. */
export function createAuthSession(options: AuthSessionOptions): AuthSession {
  const { backend, emails } = options;
  const now = options.now ?? (() => Date.now());
  const readToken = options.getIdToken ?? (() => backend.getIdToken());

  /** Apple's access token from the last Apple popup in this page's life, kept only to revoke it on delete. */
  let appleToken: string | null = null;
  /** When `reauthenticate` last succeeded, for this uid. */
  let reauthAt: { uid: string; at: number } | null = null;
  let appleRevocation: AppleRevocation | null = null;
  let starting: Promise<void> | null = null;

  const user = () => backend.currentUser();
  const hasApple = (u: AuthUser) => u.providerIds.includes(APPLE_PROVIDER_ID);
  const hasPassword = (u: AuthUser) => u.providerIds.includes(PASSWORD_PROVIDER_ID);

  /** ACC-1: whether `revokeAppleBeforeDeletion` ran for the deletion in progress, so the end does not revoke twice. */
  let revokedBeforeDeletion = false;

  /** THE ONE REVOKE (F-AC-7): while the user still exists, with the Apple re-entry's token. Null for no Apple. */
  async function revokeApple(): Promise<AppleRevocation | null> {
    const u = user();
    appleRevocation = null;
    if (u === null || !hasApple(u)) return null;
    // BEFORE the user is deleted: `revokeAccessToken` needs the current user's ID token and does nothing without one.
    if (appleToken === null) {
      appleRevocation = "noToken";
      console.error("PROppeal: APPLE_REVOKE_SKIPPED — an Apple-linked account was deleted with no Apple token held (no Apple re-entry first).");
      return appleRevocation;
    }
    try {
      await backend.revokeAppleAccessToken(appleToken);
      appleRevocation = "revoked";
    } catch (error) {
      appleRevocation = "failed";
      console.error(`PROppeal: APPLE_REVOKE_FAILED (${authErrorCode(error) ?? "no code"}) — the deletion goes on.`);
    }
    return appleRevocation;
  }

  function start(): Promise<void> {
    if (starting === null) {
      starting = ensureSignedIn(backend).finally(() => {
        starting = null;
      });
    }
    return starting;
  }

  /** Whoever is signed in, starting a look-around first if nobody is. */
  async function signedInUser(): Promise<AuthUser | null> {
    if (user() === null) await start();
    return user();
  }

  function forgetAccountState(): void {
    appleToken = null;
    reauthAt = null;
    revokedBeforeDeletion = false;
  }

  function signedInResult(lookAround: LookAround): AccountResult {
    const current = user();
    if (current === null) return refused({ kind: "unknown", code: null });
    return { ok: true, uid: current.uid, provider: providerOf(current) ?? "anonymous", lookAround };
  }

  function recentlyReauthenticated(u: AuthUser): boolean {
    return reauthAt !== null && reauthAt.uid === u.uid && now() - reauthAt.at <= REAUTH_WINDOW_MS;
  }

  /** Whether the account signed in here has a confirmed email, read fresh (a reload: the SDK's flag is stale until one).
   *  A look-around has no email to confirm, so never. */
  async function confirmedAlready(): Promise<boolean> {
    const u = await signedInUser();
    if (u === null || u.isAnonymous) return false;
    await refreshQuietly();
    return user()?.emailVerified === true;
  }

  async function refreshQuietly(): Promise<void> {
    if (user() === null) return;
    try {
      await backend.reload();
      await backend.getIdToken(true);
    } catch {
      // The link has been applied either way. A user the reload cannot refresh (a change that ended its tokens) is the
      // SDK's to sign out; the result below still says what the link did.
    }
  }

  return {
    get uid() {
      return user()?.uid ?? null;
    },
    get provider() {
      return providerOf(user());
    },
    get email() {
      return user()?.email ?? null;
    },
    get emailVerified() {
      return user()?.emailVerified ?? false;
    },
    get reauthenticateWith() {
      const u = user();
      if (u === null || u.isAnonymous) return null;
      if (hasApple(u)) return "apple";
      return hasPassword(u) ? "password" : null;
    },
    get appleRevocation() {
      return appleRevocation;
    },
    get linkedProviders() {
      const u = user();
      if (u === null || u.isAnonymous) return [];
      const ways: ("password" | "apple")[] = [];
      if (hasApple(u)) ways.push("apple");
      if (hasPassword(u)) ways.push("password");
      return ways;
    },

    getIdToken: () => readToken(),
    signIn: () => start(),
    refresh: () => refreshQuietly(),

    async createAccountWithPassword(email, password) {
      const u = await signedInUser();
      if (u === null) return refused({ kind: "notSignedIn" });
      if (!u.isAnonymous) return refused({ kind: "alreadySignedIn" });
      if (password.length < MIN_PASSWORD_LENGTH) return refused({ kind: "passwordTooShort" });
      try {
        // LINK, never a plain sign-up: the look-around's uid becomes the account's (ACCOUNTS_PLAN §2.1, §2.3).
        await backend.linkWithPassword(email, password);
      } catch (error) {
        const code = authErrorCode(error);
        if (code === "auth/email-already-in-use" || code === "auth/credential-already-in-use") {
          return refused({ kind: "emailHasAccount" });
        }
        return refused(refusalFor(error));
      }
      return signedInResult({ kind: "carriedOver" });
    },

    async signInWithPassword(email, password) {
      const u = await signedInUser();
      if (u !== null && !u.isAnonymous) return refused({ kind: "alreadySignedIn" });
      try {
        await backend.signInWithPassword(email, password);
      } catch (error) {
        return refused(refusalFor(error));
      }
      forgetAccountState();
      return signedInResult(u === null ? { kind: "none" } : { kind: "leftBehind", anonymousUid: u.uid });
    },

    async continueWithApple() {
      const u = await signedInUser();
      if (u === null) return refused({ kind: "notSignedIn" });
      if (hasApple(u)) return refused({ kind: "alreadyLinked" });
      try {
        const grant = await backend.linkWithApple();
        appleToken = grant.accessToken;
        return signedInResult({ kind: "carriedOver" });
      } catch (error) {
        const code = authErrorCode(error);
        if (code === "auth/account-exists-with-different-credential" || code === "auth/email-already-in-use") {
          return refused({ kind: "emailHasPasswordAccount" });
        }
        if (code !== "auth/credential-already-in-use") return refused(refusalFor(error));
        // THAT APPLE ID IS ALREADY AN ACCOUNT. From a look-around, it is the owner's own (the app's usual case): sign in
        // to it. From an account, it is a DIFFERENT account, and switching would be the silent second account decision 8
        // exists to prevent — so refuse, and let support combine them (C11).
        if (!u.isAnonymous) return refused({ kind: "appleIdIsAnotherAccount" });
        try {
          const grant = await backend.signInWithCredentialFromError(error);
          forgetAccountState();
          appleToken = grant.accessToken;
          return signedInResult({ kind: "leftBehind", anonymousUid: u.uid });
        } catch (signInError) {
          return refused(refusalFor(signInError));
        }
      }
    },

    async signOut() {
      try {
        await backend.signOut();
      } catch (error) {
        return { ok: false, signedOut: false, refusal: refusalFor(error) };
      }
      forgetAccountState();
      try {
        await start();
      } catch (error) {
        return { ok: false, signedOut: true, refusal: refusalFor(error) };
      }
      const fresh = user();
      if (fresh === null) return { ok: false, signedOut: true, refusal: { kind: "unknown", code: null } };
      return { ok: true, anonymousUid: fresh.uid };
    },

    async reauthenticate(proof) {
      const u = user();
      if (u === null || u.isAnonymous) return refused({ kind: "notSignedIn" });
      try {
        if ("password" in proof) {
          if (!hasPassword(u)) return refused({ kind: "wrongProvider" });
          await backend.reauthenticateWithPassword(proof.password);
        } else {
          if (!hasApple(u)) return refused({ kind: "wrongProvider" });
          const grant = await backend.reauthenticateWithApple();
          appleToken = grant.accessToken;
        }
      } catch (error) {
        return refused(refusalFor(error));
      }
      reauthAt = { uid: u.uid, at: now() };
      return { ok: true };
    },

    async changePassword(newPassword) {
      const u = user();
      if (u === null || u.isAnonymous) return refused({ kind: "notSignedIn" });
      if (!hasPassword(u)) return refused({ kind: "wrongProvider" });
      if (!recentlyReauthenticated(u)) return refused({ kind: "reauthRequired" });
      if (newPassword.length < MIN_PASSWORD_LENGTH) return refused({ kind: "passwordTooShort" });
      try {
        await backend.updatePassword(newPassword);
      } catch (error) {
        return refused(refusalFor(error));
      }
      return { ok: true, notice: await emails.sendPasswordChanged() };
    },

    async addPassword(password) {
      const u = user();
      if (u === null || u.isAnonymous || u.email === null) return refused({ kind: "notSignedIn" });
      if (hasPassword(u)) return refused({ kind: "alreadyLinked" });
      if (!recentlyReauthenticated(u)) return refused({ kind: "reauthRequired" });
      if (password.length < MIN_PASSWORD_LENGTH) return refused({ kind: "passwordTooShort" });
      try {
        // LINK, under the account's own email: the uid, and the credits under it, stay the account's.
        await backend.linkWithPassword(u.email, password);
      } catch (error) {
        return refused(refusalFor(error));
      }
      return { ok: true };
    },

    async changeEmail(newEmail) {
      const u = user();
      if (u === null || u.isAnonymous) return refused({ kind: "notSignedIn" });
      if (!recentlyReauthenticated(u)) return refused({ kind: "reauthRequired" });
      return emails.sendEmailChange(newEmail);
    },

    async sendEmailVerification() {
      const u = user();
      if (u === null || u.isAnonymous || u.email === null) return refused({ kind: "notSignedIn" });
      if (u.emailVerified) return refused({ kind: "alreadyVerified" });
      return emails.sendVerification();
    },

    sendPasswordReset: (email) => emails.sendPasswordReset(email),

    async openActionLink(link) {
      try {
        switch (link.mode) {
          case "verifyEmail":
            try {
              await backend.applyActionCode(link.oobCode);
            } catch (error) {
              // ALREADY USED, AND THE ACCOUNT IS CONFIRMED (WALK-FIX-1, R236). A mail provider's link
              // scanner can open the link before the owner does, so the owner's own click finds it spent: Tyler,
              // 2026-10-05, was told his link was expired or used while his account WAS confirmed. When Firebase refuses
              // the code as spent or expired, the signed-in account is re-read, and if ITS email is confirmed the page
              // says so (F-ACC3a-2, "Your email is confirmed."); otherwise the refusal stands (D5).
              if (refusalFor(error).kind === "linkExpired" && (await confirmedAlready())) return { ok: true, kind: "emailVerified" };
              throw error;
            }
            await refreshQuietly();
            return { ok: true, kind: "emailVerified" };
          case "verifyAndChangeEmail": {
            const info = await backend.checkActionCode(link.oobCode);
            await backend.applyActionCode(link.oobCode);
            await refreshQuietly();
            return { ok: true, kind: "emailChanged", email: info.email };
          }
          case "recoverEmail": {
            const info = await backend.checkActionCode(link.oobCode);
            await backend.applyActionCode(link.oobCode);
            await refreshQuietly();
            return { ok: true, kind: "emailRestored", email: info.email };
          }
          case "resetPassword":
            return { ok: true, kind: "choosePassword", email: await backend.verifyPasswordResetCode(link.oobCode) };
        }
      } catch (error) {
        return refused(refusalFor(error));
      }
    },

    async resetPassword(oobCode, newPassword) {
      if (newPassword.length < MIN_PASSWORD_LENGTH) return refused({ kind: "passwordTooShort" });
      try {
        await backend.confirmPasswordReset(oobCode, newPassword);
      } catch (error) {
        return refused(refusalFor(error));
      }
      return { ok: true };
    },

    async revokeAppleBeforeDeletion() {
      revokedBeforeDeletion = false;
      const revoked = await revokeApple();
      revokedBeforeDeletion = revoked !== null;
      return revoked;
    },

    async endAfterDeletion(options = {}) {
      if (options.signInDeleted === true) {
        // THE SERVER HAS DELETED THE SIGN-IN (ACC-1). Apple had to be revoked before the call; if it was not, it is too
        // late now — the user is gone — and that is an alarm, never a stop.
        const u = user();
        if (u !== null && hasApple(u) && !revokedBeforeDeletion) {
          appleRevocation = "failed";
          console.error("PROppeal: APPLE_REVOKE_TOO_LATE — the server deleted an Apple-linked sign-in before Apple was revoked (call revokeAppleBeforeDeletion first).");
        }
        try {
          await backend.signOut();
        } catch {
          // The sign-in no longer exists anywhere; a local sign-out that fails leaves only a dead token behind.
        }
        forgetAccountState();
        return "deleted";
      }
      if (!revokedBeforeDeletion) await revokeApple();
      const ended = await endAccountSession(backend);
      forgetAccountState();
      return ended;
    },
  };
}
